/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { useCallback, useMemo, useRef } from 'react';
import Link from 'next/link';
import { useLanguage } from '@/providers/LanguageProvider';
import { handleCopyLink } from '@/utils/handleCopyLink';
import { copyLabelWithFact } from '@/utils/kahuCopyLabelWithFact';
import Icons from '@/ux-common/src/icons/icons';
import { cn } from '@/ux-common/src/lib/utils';
import { kahuCopyInfoType } from 'types/kahu';

import { useTraceId } from '@/hooks/useTraceID';

import { TraceBadges } from '../display-page/traces/trace-badges';
import {
  formatDuration,
  truncateTraceId,
} from '../display-page/traces/traceUtils';
import { Separator } from '../ui/separator';
import { Skeleton } from '../ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

type KahuTraceIdTooltipProps = {
  traceId: string;
  href: string;
  occurrenceKey: string;
  copiedInfo: kahuCopyInfoType | null;
  setCopiedInfo: React.Dispatch<React.SetStateAction<kahuCopyInfoType | null>>;
};

export const KahuTraceIdTooltip = ({
  traceId,
  href,
  occurrenceKey,
  copiedInfo,
  setCopiedInfo,
}: KahuTraceIdTooltipProps) => {
  const { messages } = useLanguage();
  const {
    state: {
      traceIdSearchData,
      traceIdLoading,
      factMeasures,
      factMeasuresLoading,
    },
    fetchTraceByID,
    fetchFactMeasures,
    clearTraceIDResults,
  } = useTraceId();

  const { resolvedTraceId, factName } = useMemo(() => {
    try {
      // Parsing the URL to extract trace_id and factName query parameters
      const parsedUrl = new URL(href, window.location.origin);
      return {
        resolvedTraceId: parsedUrl.searchParams.get('trace_id') ?? traceId,
        factName: parsedUrl.searchParams.get('factName') ?? 'traces',
      };
    } catch {
      return {
        resolvedTraceId: traceId,
        factName: 'traces',
      };
    }
  }, [href, traceId]);

  const currentTrace = Array.isArray(traceIdSearchData)
    ? (traceIdSearchData.find((trace) => trace?.trace_id === resolvedTraceId) ??
      traceIdSearchData[0])
    : null;

  const workflowName =
    currentTrace?.workflow_name ||
    currentTrace?.attributes?.['workflow.name'] ||
    '';

  const traceLabels = factMeasures?.[resolvedTraceId]?.labels;
  const hasPrefetchedRef = useRef(false);

  const kahuCopiedStrWithPrefix = useMemo(
    () =>
      copyLabelWithFact({
        text: traceId,
        href,
        id: 0,
        copiedContentType: 'traceId',
      }),
    [traceId, href]
  );

  const isCopied = copiedInfo?.occurrenceKey === occurrenceKey;

  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    handleCopyLink(e, traceId, href, occurrenceKey, 'traceId', setCopiedInfo);
  };

  /**
   * Fetches measures and trace details when user hovers or focuses on the trace ID link,
   * ensuring data is ready when tooltip opens
   */
  const prefetchTooltipData = useCallback(() => {
    if (hasPrefetchedRef.current) return;

    hasPrefetchedRef.current = true;
    void fetchTraceByID(resolvedTraceId, factName, false);
    void fetchFactMeasures(factName, resolvedTraceId);
  }, [factName, fetchFactMeasures, fetchTraceByID, resolvedTraceId]);

  return (
    <TooltipProvider>
      <Tooltip
        onOpenChange={(open) => {
          if (open) {
            prefetchTooltipData();
            return;
          }

          hasPrefetchedRef.current = false;
          clearTraceIDResults();
        }}
      >
        <TooltipTrigger asChild>
          <span className="group relative inline-block">
            <Link
              href={href}
              onMouseEnter={prefetchTooltipData}
              onFocus={prefetchTooltipData}
              className="rounded bg-[#FFFFFF14] px-[3px] text-body font-normal text-body-subtle hover:text-[#fff]"
            >
              {truncateTraceId(traceId)}
            </Link>

            <button
              aria-label="Copy value"
              className={`absolute right-0 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded bg-[#08242F] transition-opacity ${
                isCopied
                  ? 'pointer-events-auto opacity-100'
                  : 'pointer-events-none opacity-0 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100'
              }`}
              onClick={handleCopy}
            >
              {isCopied ? Icons.CopyGreenCheckIcon() : Icons.CopyIcon()}
            </button>
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="relative z-[110] rounded px-3 py-4"
        >
          <div className="space-y-2">
            <p className="text-body-sm text-body-secondary">
              {kahuCopiedStrWithPrefix}
            </p>

            <div className="space-y-3">
              {traceIdLoading ? (
                <Skeleton
                  className={cn(
                    'animate-pulse-custom my-auto rounded-full bg-[#8C9EA1]',
                    'h-5 w-72'
                  )}
                />
              ) : (
                // duration and workflow name
                <>
                  {currentTrace?.duration_ms != null && workflowName && (
                    <div className="flex items-center gap-2 text-body text-body-primary">
                      <p className="w-fit whitespace-nowrap">
                        {formatDuration(currentTrace.duration_ms)}
                      </p>
                      <Separator
                        className="min-h-4 bg-line-tertiary"
                        orientation="vertical"
                      />

                      <p className="w-fit whitespace-nowrap">
                        {' '}
                        {messages?.PromptFilters?.multi_select?.workflow}:{' '}
                        {workflowName}
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* fact measure badges */}
              <TraceBadges labels={traceLabels} loading={factMeasuresLoading} />
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
